/** Cache only the manifest’s versioned slide/learning videos; never APIs or call data.
 * Cached blobs also avoid relying on Safari's partial-response HTTP cache.
 */
const CACHE = 'rifq-scenario-videos-v1';
const valid = (url:string) => /^\/video\/scenarios\/[a-z]+\.[a-f0-9]{12}\.mp4$/.test(url);
let warming:Promise<void>|undefined;
export async function cachedScenarioVideo(url:string, timeoutMs=150):Promise<string|null> {
  if(!valid(url)||!('caches' in window))return null;
  // A busy cache must not leave the screen blank. Fall back to streaming,
  // and discard a late blob rather than restarting a video already playing.
  return new Promise(resolve=>{
    let settled=false;
    const finish=(blob:string|null)=>{
      if(settled){if(blob)URL.revokeObjectURL(blob);return;}
      settled=true;clearTimeout(timer);resolve(blob);
    };
    const timer=setTimeout(()=>finish(null),timeoutMs);
    void (async()=>{
      try {
        const response=await (await caches.open(CACHE)).match(url);
        finish(response?.ok ? URL.createObjectURL(await response.blob()) : null);
      } catch {finish(null);} // Quota/private mode never prevent playback.
    })();
  });
}
export function warmScenarioVideos(first:string) {
  if(warming||!('caches' in window))return;
  warming=(async()=>{
    try {
      const manifest=await fetch('/scenario-media.json',{cache:'no-cache'}).then(r=>r.json());
      const urls:string[]=manifest.videos.filter((u:unknown)=>typeof u==='string'&&valid(u));
      const cache=await caches.open(CACHE);
      // Remove superseded versions, retaining only this release's small set.
      for(const request of await cache.keys())if(!urls.includes(new URL(request.url).pathname))await cache.delete(request);
      // Prepare short learning clips before spending bandwidth on every
      // background. The visible slide is already streaming in its player.
      const learning:string[]=(manifest.learningVideos||[]).filter((u:unknown)=>typeof u==='string'&&urls.includes(u));
      for(const url of [...new Set([...learning,first,...urls])].filter(valid)) {
        if(await cache.match(url))continue;
        // Sequential low-priority downloads, never a burst of parallel videos.
        try {
          const response=await fetch(url,{cache:'force-cache',signal:AbortSignal.timeout(90000),priority:'low'} as RequestInit);
          if(response.status===200&&response.headers.get('content-type')?.includes('video/mp4'))await cache.put(url,response);
        } catch { /* Playback falls back to the original same-origin URL. */ }
      }
    } catch { /* A missing cache/manifest must not block the audience. */ }
  })();
}
